import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";

import { cn } from "../../lib/cn";

import type { ViewDef } from "../nav";

// ---------------------------------------------------------------------------
// Animated badge — smoothly ticks up/down via CSS transitions
// ---------------------------------------------------------------------------

function AnimatedBadge({
  count,
  active,
  loading,
}: {
  count: number;
  active: boolean;
  loading?: boolean;
}) {
  const prevRef = useRef(count);
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    if (count > prevRef.current) {
      setFlash(true);
      const t = setTimeout(() => {
        setFlash(false);
      }, 600);
      prevRef.current = count;
      return () => {
        clearTimeout(t);
      };
    }
    prevRef.current = count;
  }, [count]);

  if (count === 0 && !loading) return null;

  const label =
    count > 999 ? `${String(Math.floor(count / 1000))}k` : String(count);

  return (
    <span
      className={cn(
        "ml-auto inline-flex min-w-[18px] items-center justify-center rounded-full px-1.5 py-0.5 font-mono text-[9px] font-normal tabular-nums leading-none transition-all duration-300",
        active
          ? "bg-[rgba(201,168,76,0.2)] text-[var(--color-gold)]"
          : "bg-[rgba(255,255,255,0.06)] text-[var(--color-text-muted)]",
        loading && "bg-[rgba(201,168,76,0.12)] text-[var(--color-gold)]",
        flash && "scale-110 bg-[rgba(201,168,76,0.3)] text-[var(--color-gold)]",
      )}
    >
      {loading && (
        <span className="mr-1 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-current" />
      )}
      {count > 0 ? label : "\u2026"}
    </span>
  );
}

// ---------------------------------------------------------------------------
// GroupedSideNav — shared sidebar for devtools apps
// ---------------------------------------------------------------------------

export interface GroupedSideNavProps {
  groups: readonly string[];
  views: ViewDef[];
  collapsed: boolean;
  onItemClick?: () => void;
  /** Optional badge counts keyed by view id (e.g. { txlog: 12, events: 47 }) */
  badges?: Record<string, number>;
  /** Optional loading state keyed by view id (e.g. { txlog: true, events: true }) */
  loadingBadges?: Record<string, boolean>;
}

export function GroupedSideNav({
  groups,
  views,
  collapsed,
  onItemClick,
  badges,
  loadingBadges,
}: GroupedSideNavProps) {
  const location = useLocation();

  return (
    <nav
      className={cn(
        "flex flex-col border-r border-[var(--color-border)] bg-[var(--color-surface)] transition-[width] duration-200",
        collapsed ? "w-14" : "w-60 max-w-[85vw]",
      )}
    >
      <div className="flex flex-1 flex-col gap-0.5 overflow-y-auto p-2">
        {groups.map((group) => {
          const items = views.filter((v) => v.group === group);
          if (items.length === 0) return null;
          return (
            <div key={group}>
              {collapsed ? (
                <div className="mt-3" />
              ) : (
                <div className="mb-1 mt-4 px-3 font-mono text-[10px] font-normal uppercase tracking-[0.15em] text-[var(--color-text-muted)]">
                  {group}
                </div>
              )}
              {items.map((item) => {
                // Check if another sibling nav item has a more specific match
                const hasMoreSpecificMatch =
                  item.path !== "/" &&
                  items.some(
                    (other) =>
                      other.id !== item.id &&
                      other.path.startsWith(item.path + "/") &&
                      (location.pathname === other.path ||
                        location.pathname.startsWith(other.path + "/")),
                  );
                const isActive =
                  !hasMoreSpecificMatch &&
                  (location.pathname === item.path ||
                    (item.path !== "/" &&
                      location.pathname.startsWith(item.path + "/")));
                const Icon = item.icon;
                const badgeCount = badges?.[item.id] ?? 0;
                const badgeLoading = loadingBadges?.[item.id] ?? false;
                return (
                  <NavLink
                    key={item.id}
                    to={item.path}
                    title={collapsed ? item.label : undefined}
                    onClick={onItemClick}
                    className={cn(
                      "flex w-full items-center py-2 text-left transition-colors duration-150",
                      collapsed
                        ? "relative justify-center px-0"
                        : "gap-2.5 px-3",
                      isActive
                        ? "border-l-2 border-[var(--color-gold)] bg-[rgba(201,168,76,0.06)] text-[var(--color-gold)]"
                        : "border-l-2 border-transparent text-[var(--color-text-dim)] hover:bg-[rgba(201,168,76,0.03)] hover:text-[var(--color-text)]",
                    )}
                  >
                    <Icon
                      size={16}
                      strokeWidth={1.8}
                      className={cn(
                        isActive ? "" : "opacity-70",
                        collapsed && "shrink-0",
                      )}
                    />
                    {!collapsed && (
                      <>
                        <span className="truncate font-sans text-[13px] font-light tracking-normal">
                          {item.label}
                        </span>
                        {(badgeCount > 0 || badgeLoading) && (
                          <AnimatedBadge
                            count={badgeCount}
                            active={isActive}
                            loading={badgeLoading}
                          />
                        )}
                      </>
                    )}
                    {collapsed && (badgeCount > 0 || badgeLoading) && (
                      <span
                        className={cn(
                          "absolute right-1 top-0.5 h-1.5 w-1.5 rounded-full bg-[var(--color-gold)]",
                          badgeLoading && "animate-pulse",
                        )}
                      />
                    )}
                  </NavLink>
                );
              })}
            </div>
          );
        })}
      </div>
    </nav>
  );
}
